// Kardex de inventario: movimientos por bodega / producto / rango de fechas.
// Inventario → Reportes → Kardex.
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, ScrollText, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { BuscadorProducto, Campo, Location, ProductoOpt, fieldCls, invRpc, locationsList, money, num, productosOptList } from './shared';
import { LOCALE } from '@/lib/utils/regional';
import { useTranslation } from 'react-i18next';

interface KardexRow {
  fecha: string; location_nombre: string; producto_codigo: string; producto_nombre: string;
  lote: string | null; tipo_movimiento: string; entrada: number; salida: number;
  costo_unitario: number; costo_total: number; saldo_unidades: number; saldo_costo_promedio: number; saldo_valor: number;
  documento: string | null;
}

export function Kardex() {
  const { t } = useTranslation('inventario');
  const { data: bodegas = [] } = useQuery<Location[]>({ queryKey: ['inv-locations'], queryFn: locationsList });
  const { data: productos = [] } = useQuery<ProductoOpt[]>({ queryKey: ['inv-productos-opt'], queryFn: productosOptList });

  const [f, setF] = useState({ location: '', producto: '', desde: '', hasta: '' });
  const [filtros, setFiltros] = useState(f);
  const set = (patch: Partial<typeof f>) => setF((s) => ({ ...s, ...patch }));

  const { data: rows = [], isFetching } = useQuery<KardexRow[]>({
    queryKey: ['inv-kardex', filtros],
    queryFn: () => invRpc<KardexRow[]>('inv_kardex', {
      p_location: filtros.location || null, p_producto: filtros.producto || null,
      p_desde: filtros.desde || null, p_hasta: filtros.hasta || null,
    }),
  });

  const exportFilas = useMemo<Celda[][]>(() => {
    const filas: Celda[][] = rows.map((r, i) => [i + 1, new Date(r.fecha).toLocaleDateString(LOCALE), r.location_nombre,
      r.producto_codigo, r.producto_nombre, r.lote, r.tipo_movimiento,
      r.entrada ? Number(r.entrada) : null, r.salida ? Number(r.salida) : null, Number(r.costo_unitario),
      Number(r.saldo_unidades), Number(r.saldo_valor), r.documento]);
    if (filas.length) filas.push(['', t('comun.totales'), '', '', '', '', '',
      rows.reduce((a, r) => a + Number(r.entrada || 0), 0), rows.reduce((a, r) => a + Number(r.salida || 0), 0),
      null, null, null, '']);
    return filas;
  }, [rows, t]);

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><ScrollText className="w-5 h-5" /> {t('kardex.titulo')}</h1>
      <Card>
        <CardContent className="grid grid-cols-2 md:grid-cols-5 gap-3 pt-4 items-end">
          <Campo label={t('kardex.bodega')}>
            <select className={fieldCls} value={f.location} onChange={(e) => set({ location: e.target.value })}>
              <option value="">{t('kardex.todas')}</option>
              {bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
            </select>
          </Campo>
          <Campo label={t('kardex.producto')} className="md:col-span-2">
            <BuscadorProducto productos={productos} value={f.producto} allowClear
              placeholder={t('kardex.todos')} onChange={(id) => set({ producto: id })} />
          </Campo>
          <Campo label={t('kardex.desde')}><input type="date" className={fieldCls} value={f.desde} onChange={(e) => set({ desde: e.target.value })} /></Campo>
          <Campo label={t('kardex.hasta')}><input type="date" className={fieldCls} value={f.hasta} onChange={(e) => set({ hasta: e.target.value })} /></Campo>
          <div className="md:col-span-5"><Button onClick={() => setFiltros(f)}><Search className="w-4 h-4 mr-1" /> {t('kardex.consultar')}</Button></div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">{t('kardex.movimientos', { n: rows.length })}</CardTitle>
          <ExportarBotones nombre="kardex" titulo={t('kardex.titulo_export')}
            headers={[t('comun.n_col'), t('kardex.fecha'), t('kardex.bodega'), t('kardex.codigo'), t('kardex.producto'), t('kardex.lote'), t('kardex.movimiento'), t('kardex.entrada'), t('kardex.salida'), t('kardex.costo_unit'), t('kardex.saldo'), t('kardex.valor_saldo'), t('kardex.doc')]}
            filas={exportFilas} colsDerecha={[0, 7, 8, 9, 10, 11]} />
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">{t('kardex.fecha')}</th><th className="py-2 pr-3">{t('kardex.bodega')}</th><th className="py-2 pr-3">{t('kardex.producto')}</th>
              <th className="py-2 pr-3">{t('kardex.lote')}</th><th className="py-2 pr-3">{t('kardex.movimiento')}</th>
              <th className="py-2 pr-3 text-right">{t('kardex.entrada')}</th><th className="py-2 pr-3 text-right">{t('kardex.salida')}</th>
              <th className="py-2 pr-3 text-right">{t('kardex.costo_unit')}</th><th className="py-2 pr-3 text-right">{t('kardex.saldo')}</th>
              <th className="py-2 pr-3 text-right">{t('kardex.c_prom')}</th><th className="py-2 pr-3 text-right">{t('kardex.valor_saldo')}</th><th className="py-2 pr-3">{t('kardex.doc')}</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={12} className="py-8 text-center text-muted-foreground">{t('kardex.sin_movimientos')}</td></tr>}
              {rows.map((r, i) => (
                <tr key={i} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 whitespace-nowrap">{new Date(r.fecha).toLocaleDateString(LOCALE)}</td>
                  <td className="py-2 pr-3">{r.location_nombre}</td>
                  <td className="py-2 pr-3"><span className="font-mono text-xs">{r.producto_codigo}</span> {r.producto_nombre}</td>
                  <td className="py-2 pr-3">{r.lote ?? '—'}</td>
                  <td className="py-2 pr-3"><span className="rounded-full bg-muted px-2 py-0.5 text-xs">{r.tipo_movimiento}</span></td>
                  <td className="py-2 pr-3 text-right text-emerald-600">{r.entrada ? num(r.entrada, 2) : ''}</td>
                  <td className="py-2 pr-3 text-right text-red-600">{r.salida ? num(r.salida, 2) : ''}</td>
                  <td className="py-2 pr-3 text-right">{money(r.costo_unitario)}</td>
                  <td className="py-2 pr-3 text-right font-medium">{num(r.saldo_unidades, 2)}</td>
                  <td className="py-2 pr-3 text-right">{money(r.saldo_costo_promedio)}</td>
                  <td className="py-2 pr-3 text-right">{money(r.saldo_valor)}</td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground">{r.documento ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
